import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useSuperadminDashboard } from '@/hooks/useSuperadmin';

const PLAN_CONFIG: Record<string, { price: number; userLimit: number; label: string }> = {
  trial:      { price: 0,    userLimit: 5,   label: 'Trial' },
  starter:    { price: 49,   userLimit: 20,  label: 'Starter' },
  pro:        { price: 299,  userLimit: 100, label: 'Pro' },
  enterprise: { price: 999,  userLimit: 9999, label: 'Enterprise' },
};

const SuperadminBilling = () => {
  const { tenants, loading, totalUsers } = useSuperadminDashboard();

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  const mrr = tenants.reduce((sum, t) => sum + (PLAN_CONFIG[t.plan]?.price ?? 0), 0);
  const overLimit = tenants.filter(t => {
    const cfg = PLAN_CONFIG[t.plan];
    return cfg && Number(t.total_users) > cfg.userLimit;
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Billing Overview</h1>
        <p className="text-muted-foreground text-sm">Usage and revenue tracking across all organisations</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Estimated MRR</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">${mrr.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Based on plan tiers</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Paying Orgs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{tenants.filter(t => t.plan !== 'trial').length}</div>
            <p className="text-xs text-muted-foreground mt-1">of {tenants.length} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              Over Limit
              {overLimit.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{overLimit.length}</div>
            <p className="text-xs text-muted-foreground mt-1">orgs exceeding user limit</p>
          </CardContent>
        </Card>
      </div>

      {/* Per-tenant billing table */}
      <Card>
        <CardHeader>
          <CardTitle>Per-Organisation Usage</CardTitle>
          <CardDescription>User count vs. plan limit — upgrade opportunities highlighted</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Organisation</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Plan</th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">Users</th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">Limit</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Usage</th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">MRR</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {tenants.map((t) => {
                const cfg = PLAN_CONFIG[t.plan] ?? PLAN_CONFIG.trial;
                const users = Number(t.total_users);
                const pct = Math.min(Math.round((users / cfg.userLimit) * 100), 100);
                const isOver = users > cfg.userLimit;
                const isNearLimit = !isOver && pct >= 80;
                return (
                  <tr key={t.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">/{t.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="secondary" className="capitalize">{cfg.label}</Badge>
                    </td>
                    <td className="px-6 py-4 text-end font-semibold">{users}</td>
                    <td className="px-6 py-4 text-end text-muted-foreground">
                      {cfg.userLimit === 9999 ? '∞' : cfg.userLimit}
                    </td>
                    <td className="px-6 py-4 w-36">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${isOver ? 'bg-destructive' : isNearLimit ? 'bg-amber-500' : 'bg-primary'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-8 text-end">{cfg.userLimit === 9999 ? '—' : `${pct}%`}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-end font-semibold">
                      {cfg.price > 0 ? `$${cfg.price}` : <span className="text-muted-foreground">Free</span>}
                    </td>
                    <td className="px-6 py-4">
                      {isOver ? (
                        <div className="flex items-center gap-1 text-destructive text-xs font-medium">
                          <AlertTriangle className="h-3.5 w-3.5" /> Over limit
                        </div>
                      ) : isNearLimit ? (
                        <div className="flex items-center gap-1 text-amber-500 text-xs font-medium">
                          <TrendingUp className="h-3.5 w-3.5" /> Near limit
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-green-500 text-xs font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5" /> OK
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tenants.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">No organisations yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminBilling;
